/**
 * Super-admin dashboard helpers. Everything is derived from the live
 * `GET /workspaces` payload (`workspace_stats`) — no static fixtures.
 */

const HEALTH_LABELS = {
  healthy: 'Healthy',
  at_risk: 'At risk',
  critical: 'Critical',
  unknown: 'Unknown',
}

/** `Badge` variants from `components/ui/badge`. */
const HEALTH_BADGE_VARIANT = {
  healthy: 'success',
  at_risk: 'warning',
  critical: 'danger',
  unknown: 'neutral',
}

const HEALTH_BAR_CLASS = {
  healthy: 'bg-success',
  at_risk: 'bg-warning',
  critical: 'bg-destructive',
  unknown: 'bg-muted-foreground/40',
}

const LIFECYCLE_LABELS = {
  active: 'Active',
  onboarding: 'Onboarding',
  inactive: 'Inactive',
  suspended: 'Suspended',
}

export const HEALTH_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'at_risk', label: 'At risk' },
  { value: 'healthy', label: 'Healthy' },
]

const num = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

/** Collapse whatever the API sends for `overall_health` into one of our three buckets. */
export function normalizeHealth(value) {
  const v = String(value ?? '').toLowerCase().replace(/[\s-]+/g, '_')
  if (!v || v === 'unknown' || v === 'n_a' || v === 'na') return 'unknown'
  if (v === 'healthy' || v === 'good' || v === 'compliant') return 'healthy'
  if (v.includes('risk') || v.includes('warning') || v.includes('attention')) return 'at_risk'
  if (v.includes('critical') || v.includes('high') || v.includes('overdue') || v.includes('poor')) {
    return 'critical'
  }
  return 'unknown'
}

/**
 * When the API omits or sends "Unknown" for `overall_health`, infer from open
 * issues / overdue items so the table does not show a useless Unknown badge.
 */
export function deriveHealth(stats = {}) {
  const fromApi = normalizeHealth(stats.overall_health)
  if (fromApi !== 'unknown') return fromApi

  const overdue = num(stats.policies?.overdue) + num(stats.trainings?.overdue)
  const openItems =
    num(stats.alerts?.open_alerts_total) || num(stats.issues_count)
  if (openItems >= 3 || overdue >= 3) return 'critical'
  if (openItems > 0 || overdue > 0) return 'at_risk'
  return 'healthy'
}

export const healthLabel = (health) => HEALTH_LABELS[health] ?? HEALTH_LABELS.unknown
export const healthBadgeVariant = (health) => HEALTH_BADGE_VARIANT[health] ?? 'neutral'
export const healthBarClass = (health) => HEALTH_BAR_CLASS[health] ?? HEALTH_BAR_CLASS.unknown

export const lifecycleLabel = (status) =>
  LIFECYCLE_LABELS[status] ?? (status ? String(status).replace(/_/g, ' ') : 'Unknown')

/** Flatten one API workspace into the row shape the dashboard renders. */
export function toWorkspaceRow(workspace) {
  const stats = workspace?.workspace_stats ?? {}
  const openAlerts =
    num(stats.alerts?.open_alerts_total ?? workspace.total_alerts) ||
    num(stats.issues_count)

  return {
    id: workspace.id,
    name: workspace.name ?? 'Untitled workspace',
    legalName: workspace.legal_name ?? '',
    lifecycle: String(workspace.status ?? 'unknown').toLowerCase(),
    health: deriveHealth(stats),
    activePolicies: num(
      stats.policies?.active ?? stats.policies?.total ?? workspace.total_policies,
    ),
    completedTrainings: num(
      stats.trainings?.completed ??
        stats.trainings?.total ??
        stats.training_courses ??
        workspace.total_trainings,
    ),
    openAlerts,
    officer: workspace.cco?.name ?? workspace.cco?.full_name ?? '',
  }
}

const SEVERITY = { critical: 0, at_risk: 1, unknown: 2, healthy: 3 }

/** Worst health first, then most open alerts, then name. */
export function compareWorkspaceRows(a, b) {
  return (
    SEVERITY[a.health] - SEVERITY[b.health] ||
    b.openAlerts - a.openAlerts ||
    a.name.localeCompare(b.name)
  )
}

/** Fleet-wide roll-up used by summary totals. */
export function summarizeWorkspaces(rows) {
  const summary = {
    total: rows.length,
    activePolicies: 0,
    completedTrainings: 0,
    openAlerts: 0,
    health: { critical: 0, at_risk: 0, healthy: 0, unknown: 0 },
    lifecycle: {},
  }
  for (const row of rows) {
    summary.activePolicies += row.activePolicies
    summary.completedTrainings += row.completedTrainings
    summary.openAlerts += row.openAlerts
    summary.health[row.health] += 1
    summary.lifecycle[row.lifecycle] = (summary.lifecycle[row.lifecycle] ?? 0) + 1
  }
  return summary
}

/**
 * Segments for the per-workspace status breakdowns. `key` is the field in the
 * `charts.bar.*_status_by_workspace` payload; `barClass` colors the segment.
 */
export const POLICY_STATUS_SEGMENTS = [
  { key: 'active', label: 'Active', barClass: 'bg-success' },
  { key: 'renew', label: 'In review', barClass: 'bg-warning' },
  { key: 'overdue', label: 'Pending renewal', barClass: 'bg-destructive' },
]

export const TRAINING_STATUS_SEGMENTS = [
  { key: 'completed', label: 'Completed', barClass: 'bg-success' },
  { key: 'pending', label: 'In progress', barClass: 'bg-warning' },
  { key: 'overdue', label: 'Overdue', barClass: 'bg-destructive' },
]

/**
 * Flatten `{ [workspace]: [{ ...counts }] }` into rows sorted by total
 * (largest first). Workspaces with nothing to show are dropped.
 */
export function toStatusBreakdown(byWorkspace, segments) {
  return Object.entries(byWorkspace ?? {})
    .map(([workspace, entries]) => {
      const latest = (Array.isArray(entries) ? entries[0] : entries) ?? {}
      const counts = Object.fromEntries(segments.map((s) => [s.key, num(latest[s.key])]))
      const total = segments.reduce((sum, s) => sum + counts[s.key], 0)
      return { workspace, counts, total }
    })
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total || a.workspace.localeCompare(b.workspace))
}

export function sharePct(count, total) {
  return total > 0 ? Math.round((count / total) * 100) : 0
}

/**
 * Placeholder payload for `GetPlatformDistributions` until the backend ships
 * `/dashboard/charts/distributions`. Same shape the pie charts consume:
 * `{ total, series: [{ label, data, percentage, color }] }`.
 */
const withShare = (series) => {
  const total = series.reduce((sum, item) => sum + item.data, 0)
  return {
    total,
    series: series.map((item) => ({ ...item, percentage: sharePct(item.data, total) })),
  }
}

export const PLACEHOLDER_DISTRIBUTIONS = {
  policies: withShare([
    { label: 'Active', data: 22, color: 'var(--success)' },
    { label: 'Renew', data: 16, color: 'var(--warning)' },
    { label: 'Overdue', data: 27, color: 'var(--destructive)' },
  ]),
  trainings: withShare([
    { label: 'Completed', data: 58, color: 'var(--success)' },
    { label: 'In progress', data: 31, color: 'var(--info)' },
    { label: 'Not started', data: 19, color: 'var(--muted-foreground)' },
    { label: 'Overdue', data: 7, color: 'var(--destructive)' },
  ]),
  audits: withShare([
    { label: 'Completed', data: 9, color: 'var(--success)' },
    { label: 'In progress', data: 6, color: 'var(--info)' },
    { label: 'Awaiting review', data: 4, color: 'var(--warning)' },
    { label: 'Not started', data: 3, color: 'var(--muted-foreground)' },
  ]),
}
